import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/ui/card";
import { Badge } from "./ui/ui/badge";
import { Terminal, HelpCircle, Zap, Users, Code, Coffee } from "lucide-react";
import { cn } from "../lib/utils";

const FAQ: React.FC = () => {
  const faqData = [
    {
      id: "what-is-hackai",
      question: "./info --about",
      shortQuestion: "What is HackAI?",
      answer:
        "HackAI is Austin's premier AI community meetup where builders, entrepreneurs, and enthusiasts come together to learn, network, and collaborate. Each 2-hour event features expert speakers, AI demos, and meaningful connections that accelerate your projects and career.",
      icon: Terminal,
      color: "text-terminal-green"
    },
    {
      id: "skill-level",
      question: "./check --requirements",
      shortQuestion: "Do I need to be an AI expert?",
      answer:
        "Not at all! HackAI welcomes developers of all skill levels. Whether you're a beginner curious about AI or an experienced developer, you'll find value in the collaborative environment and learn from others.",
      icon: Users,
      color: "text-neon-blue"
    },
    {
      id: "what-to-bring",
      question: "./prepare --equipment",
      shortQuestion: "What should I bring?",
      answer:
        "Bring your laptop, charger, and enthusiasm! We'll provide the rest – including snacks, drinks, and a collaborative workspace. Optional: favorite IDE setup, project ideas, or specific AI tools you want to explore.",
      icon: Code,
      color: "text-matrix-green"
    },
    {
      id: "cost",
      question: "./price --check",
      shortQuestion: "Is there a cost to participate?",
      answer:
        "No, HackAI is completely free to attend. We believe in keeping barriers low for learning and collaboration. All snacks, drinks, and workspace access are included at no charge.",
      icon: Zap,
      color: "text-cyber-purple"
    },
    {
      id: "existing-projects",
      question: "./projects --existing",
      shortQuestion: "Can I work on existing projects?",
      answer:
        "Absolutely! You can work on existing projects, start something new, or collaborate with others on their ideas. The goal is to learn and build together. We encourage both solo work and team collaboration.",
      icon: Coffee,
      color: "text-terminal-green"
    },
    {
      id: "mentorship",
      question: "./help --mentors",
      shortQuestion: "Will there be guidance available?",
      answer:
        "Yes! We'll have experienced developers and AI practitioners available to help guide projects, answer questions, and provide technical support. Don't hesitate to ask for help – that's what we're here for!",
      icon: HelpCircle,
      color: "text-neon-blue"
    },
  ];

  return (
    <section
      id="faq"
      className="py-20 px-4 bg-gradient-to-br from-background via-muted/5 to-background relative overflow-hidden"
    >
      {/* Floating particles background */}
      <div className="absolute inset-0 opacity-10">
        {Array.from({ length: 15 }).map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${2 + i * 0.3}s`
            }}
          >
            <Terminal className="h-4 w-4 text-primary" />
          </div>
        ))}
      </div>
      
      <div className="container max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12 space-y-4">
          <Badge variant="outline" className="border-primary text-primary">
            <HelpCircle className="h-3 w-3 mr-1" />
            Knowledge Base
          </Badge>
          <h2 className="text-3xl lg:text-5xl font-bold font-mono text-primary" data-text="./faq --list">
            ./faq --list
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Quick answers to common questions about HackAI. Run <code className="text-primary font-mono">./faq --help</code> for more information.
          </p>
        </div>

        {/* FAQ Accordion */}
        <Card className="bg-card/30 backdrop-blur-sm border-border/50">
          <CardHeader className="text-center">
            <CardTitle className="font-mono text-primary flex items-center justify-center space-x-2">
              <Terminal className="h-5 w-5" />
              <span>system.faq()</span>
            </CardTitle>
            <CardDescription className="font-mono text-xs text-muted-foreground">
              Interactive documentation system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="space-y-2">
              {faqData.map((faq) => {
                const IconComponent = faq.icon;
                return (
                  <AccordionItem 
                    key={faq.id} 
                    value={faq.id}
                    className="border border-border/50 rounded-lg overflow-hidden bg-card/20 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group"
                  >
                    <AccordionTrigger 
                      className="px-4 py-3 hover:no-underline hover:bg-primary/5 transition-all duration-300 group"
                    >
                      <div className="flex items-center space-x-3 text-left">
                        <IconComponent className={cn(
                          "h-5 w-5 transition-colors duration-300",
                          faq.color,
                          "group-hover:text-primary"
                        )} />
                        <div className="space-y-1">
                          <div className="font-mono text-sm font-semibold text-foreground">
                            {faq.question}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {faq.shortQuestion}
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 pt-2">
                      <div className="border-l-2 border-primary/30 pl-4 space-y-2">
                        <div className="font-mono text-xs text-primary opacity-60">
                          $ output:
                        </div>
                        <p className="text-muted-foreground leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>

        {/* Footer note */}
        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground font-mono">
            Still have questions? Run <code className="text-primary">./contact --help</code> or email us directly.
          </p>
        </div>
      </div>
    </section>
  );
};

export default FAQ;